import {getLocalizedText, texts, placeholders} from '../../../constants/TextConst';
import {ReactNode} from 'react';
import {messageType, messagesIds} from '../consts';
import messagesText, {moreInfoText} from '../texts';
import {resetReactMessageHidden, restartProc} from '../../GWTBridge/invoke/genPage';
import {startEntity} from '../../GWTBridge/invoke/genPage';
import {PriorityIcon} from '@priority-software/priority-style-react';

export interface MessageProps {
    id?: string;
    messageIcon?: PriorityIcon;
    title?: string;
    content?: string | ReactNode;
    confirmButtonText?: string;
    isRemovable?: boolean;
    onClose?: () => Promise<void> | void;
    onLinkClick?: any | any[];
    linkData?: any[];
    moreInfo?: string;
    moreInfoLinks?: any[];
    closable?: boolean;
    moreInfoTxt?: string;
}

type messageSize = 'small' | 'large';

export interface messageUIProps extends MessageProps {
    type: messageType;
    size?: messageSize;
    buttons: any[];
    maskClosable?: boolean;
    defaultButton?: number;
}

export interface ConfirmProps extends MessageProps {
    cancleButtonText?: string;
    confirmButtonText?: string;
    onConfirm: () => Promise<void> | void;
    onCancel?: () => Promise<void> | void;
    defaultButton?: number;
    entityName?: string;
    entityType?: string;
    isAlertMessage?: boolean;
}

let modalCallback: (messageProps: messageUIProps) => void;
let checkCallback;

export function registerModalCallbacks(callback, checkClb) {
    if (!modalCallback) {
        modalCallback = callback;
    }
    if (!checkCallback) {
        checkCallback = checkClb;
    }
}

function isSetOnLocalStorage(id) {
    return id && localStorage.getItem(id) === 'true';
}

function invokeModalCallback(messageProps) {
    if (modalCallback) {
        modalCallback(messageProps);
    } else {
        resetReactMessageHidden();
    }
}

export function showConfirmMessage(props: ConfirmProps) {
    const {
        id,
        content,
        title,
        confirmButtonText,
        cancleButtonText,
        isRemovable,
        onConfirm,
        onCancel,
        onClose,
        linkData,
        onLinkClick,
        defaultButton,
        moreInfo,
        moreInfoLinks,
        messageIcon
    } = props;
    const buttons = [
        {text: cancleButtonText, handler: onCancel},
        {text: confirmButtonText, handler: onConfirm}
    ];

    const msgProps = {
        type: messageType.confirm,
        size: 'small' as messageSize,
        messageIcon,
        title,
        content,
        buttons,
        id,
        isRemovable,
        onClose,
        linkData,
        onLinkClick,
        defaultButton,
        moreInfo,
        moreInfoLinks
    };
    if (isSetOnLocalStorage(id)) {
        onConfirm();
    } else {
        invokeModalCallback(msgProps);
    }
}

export const showAboutMessage = (props: MessageProps) => {
    const {content, title, confirmButtonText, moreInfoTxt, onClose} = props;
    const buttons = [
        {text: confirmButtonText ? confirmButtonText : messagesText.accept, handler: onClose}
    ];

    const msgProps = {
        id: messagesIds.screens.about,
        type: messageType.info,
        size: 'small' as messageSize,
        title,
        content,
        buttons,
        isRemovable: false,
        onClose,
        moreInfoTxt
    };
    invokeModalCallback(msgProps);
};

export function showInfoMessage(props: MessageProps) {
    const {
        id,
        content,
        title,
        confirmButtonText,
        isRemovable,
        onClose,
        linkData,
        onLinkClick,
        moreInfo,
        moreInfoLinks
    } = props;
    const buttons = [
        {
            text: confirmButtonText ? confirmButtonText : messagesText.accept,
            handler: onClose
        }
    ];

    const msgProps = {
        type: messageType.info,
        size: 'small' as messageSize,
        title,
        content,
        buttons,
        id,
        isRemovable,
        onClose,
        linkData,
        onLinkClick,
        moreInfo,
        moreInfoLinks
    };
    if (isSetOnLocalStorage(id)) onClose();
    else invokeModalCallback(msgProps);
}

export function showLargeMessage(props: MessageProps) {
    const {
        id,
        content,
        title,
        confirmButtonText,
        linkData,
        isRemovable,
        moreInfo,
        onLinkClick,
        onClose
    } = props;
    const buttons = [{text: confirmButtonText, handler: onClose}];

    const msgProps = {
        type: messageType.info,
        size: 'large' as messageSize,
        title,
        content,
        onLinkClick,
        linkData,
        buttons,
        id,
        isRemovable,
        onClose,
        moreInfo
    };

    invokeModalCallback(msgProps);
}

export async function showErrorMessage(props: ConfirmProps) {
    const {
        id,
        content,
        title,
        confirmButtonText,
        onConfirm,
        linkData,
        onLinkClick,
        onClose,
        moreInfo,
        moreInfoLinks,
        entityName,
        entityType,
        messageIcon,
        isAlertMessage
    } = props;
    let buttons = [{text: confirmButtonText, handler: onConfirm}];

    if (entityName) {
        buttons = [
            {text: messagesText.confirmAndClose, handler: onConfirm},
            {
                text: messagesText.backToProc,
                handler: () => {
                    restartProc(entityName, entityType);
                }
            }
        ];
    }

    const msgProps = {
        type: messageType.error,
        size: 'small' as messageSize,
        title,
        content,
        buttons,
        id,
        linkData,
        onClose,
        onLinkClick,
        moreInfo,
        moreInfoLinks,
        messageIcon: isAlertMessage ? PriorityIcon.propertyError : messageIcon,
        isAlertMessage
    };

    if (isSetOnLocalStorage(id)) onConfirm();
    else invokeModalCallback(msgProps);
}

export function showWarnMessage(props: MessageProps) {
    const {id, content, title, confirmButtonText, isRemovable, moreInfo, onClose} = props;
    const buttons = [
        {text: confirmButtonText ? confirmButtonText : messagesText.accept, handler: onClose}
    ];

    const msgProps = {
        type: messageType.error,
        size: 'small' as messageSize,
        title,
        content,
        buttons,
        id,
        isRemovable,
        onClose,
        moreInfo
    };
    if (isSetOnLocalStorage(id)) onClose();
    else invokeModalCallback(msgProps);
}

export function showShouldExitMessage(onConfirm, onCancel?, onClose?, isRemovable = false, id?) {
    const msgProps = {
        id: id,
        title: messagesText.justMoment,
        isRemovable: isRemovable,
        content: messagesText.searchStopsShouldExit,
        moreInfo: moreInfoText?.searchStopsShouldExit,
        confirmButtonText: messagesText.yesExitSearch,
        cancleButtonText: messagesText.cancel,
        defaultButton: 0,
        onConfirm,
        onCancel,
        onClose
    };

    showConfirmMessage(msgProps);
}

export function showUnsavedChangesMessages(title, onConfirm, onCancel?, onClose?) {
    const msgProps = {
        title,
        isRemovable: false,
        confirmButtonText: messagesText.yes,
        cancleButtonText: messagesText.noChangedMyMind,
        onConfirm,
        onCancel,
        onClose
    };

    showConfirmMessage(msgProps);
}
export function showOnDeleteRecord(onConfirm, onCancel?) {
    const msgProps = {
        title: messagesText.deletItem,
        content: messagesText.deleteData,
        moreInfo: moreInfoText?.deleteData,
        confirmButtonText: messagesText.yes,
        cancleButtonText: messagesText.cancel,
        onConfirm: onConfirm,
        onCancel: onCancel,
        onClose: onCancel,
        defaultButton: 0
    };

    showConfirmMessage(msgProps);
}

export function showOnCrtlZMessages(onConfirm?, onCancel?) {
    const msgProps = {
        id: messagesIds.screens.ctrlZConfirm,
        title: messagesText.ctrlZTitle,
        content: messagesText.ctrlZContent,
        moreInfo: moreInfoText?.ctrlZContent,
        isRemovable: true,
        confirmButtonText: messagesText.yesDeleteChanges,
        cancleButtonText: messagesText.dontDelete,
        onConfirm: onConfirm,
        onCancel: onCancel,
        onClose: onCancel,
        defaultButton: 0
    };

    showConfirmMessage(msgProps);
}

export function showHelpMessage(content, onClose?, moreInfo?) {
    const msgProps = {
        title: messagesText.priorityHelp,
        content,
        moreInfo,
        isRemovable: false,
        confirmButtonText: messagesText.accept,
        onClose
    };
    showLargeMessage(msgProps);
}

export function showHelpWithLinksMessage(content, linkData, onClose?, moreInfo?) {
    const msgProps = {
        title: messagesText.priorityHelp,
        content,
        linkData,
        moreInfo,
        onLinkClick: linkData => {
            startEntity(linkData.ename, null, null);
        },
        isRemovable: false,
        confirmButtonText: messagesText.accept,
        onClose
    };
    showLargeMessage(msgProps);
}

export function showTitleConfirm(id, entityName, onConfirm, onCancel) {
    const title = getLocalizedText(texts.nameDoubleWarn).replace(
        placeholders.entityName,
        entityName
    );

    const msgProps = {
        id,
        title,
        isRemovable: true,
        confirmButtonText: getLocalizedText(texts.nameDoubleOk),
        cancleButtonText: getLocalizedText(texts.nameDoubleCancel),
        onConfirm,
        onCancel,
        onClose: onCancel
    };

    showConfirmMessage(msgProps);
}

export function displayDependentPacksConfirm(
    activeFieldPackTitle,
    packagesTitles,
    msg,
    onConfirm?,
    onCancel?
) {
    let dependentPacks = '';
    for (const packTitle of packagesTitles) {
        dependentPacks = `${dependentPacks}${dependentPacks.length > 0 ? ',' : ''} ${
            packTitle.packtitle ? packTitle.packtitle : packTitle
        }`;
    }
    // let msg = hide ? getLocalizedText(texts.hidePackWarn) : getLocalizedText(texts.showPackWarn);
    const _msg =
        msg &&
        msg
            .replace(placeholders.activeFieldPackTitle, activeFieldPackTitle)
            .replace(placeholders.dependentPacks, dependentPacks);

    const msgProps = {
        title: _msg,
        isRemovable: false,
        confirmButtonText: messagesText.accept,
        cancleButtonText: messagesText.noChangedMyMind,
        onConfirm,
        onCancel,
        onClose: onCancel
    };

    showConfirmMessage(msgProps);
}

export function showOfflineErrorMessage(props: ConfirmProps) {
    const {
        id,
        content,
        title,
        confirmButtonText,
        onConfirm,
        linkData,
        onLinkClick,
        onClose,
        moreInfo,
        moreInfoLinks,
        entityName,
        entityType,
        closable
    } = props;

    let buttons = [{text: confirmButtonText, handler: onConfirm}];

    if (entityName) {
        buttons = [
            {text: messagesText.confirmAndClose, handler: onConfirm},
            {
                text: messagesText.backToProc,
                handler: () => {
                    restartProc(entityName, entityType);
                }
            }
        ];
    }

    const msgProps = {
        type: messageType.none,
        size: 'small' as messageSize,
        title,
        content,
        buttons,
        id,
        linkData,
        onClose,
        onLinkClick,
        moreInfo,
        closable,
        moreInfoLinks
    };

    if (isSetOnLocalStorage(id)) onConfirm();
    else invokeModalCallback(msgProps);
}

export function processGWTMessages(ds) {
    console.log(ds);
}
